import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Grid,
  Container,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import SecurityIcon from '@mui/icons-material/Security';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useNavigate } from 'react-router-dom';
import { ACCOUNTING_API, handleApiError } from '../../../config/api';
import { accountingApi } from '../../../config/apiClient';
import { useAuth } from '../../../contexts/AuthContext';
import AccountingDataTable from '../shared/AccountingDataTable';
import type { DataTableColumn } from '../shared/AccountingDataTable';
import { useTranslation } from 'react-i18next';

interface PavoPayIntegrationItem {
  id: number;
  branchId: number | null;
  branchName: string | null;
  username: string;
  encryptedPassword: string;
  isActive: boolean;
  lastAuthDate: string | null;
  createdAt: string;
  updatedAt: string | null;
}

const PavoPayIntegrations: React.FC = () => {
  const navigate = useNavigate();
  const { userDetails, branches, loadBranches } = useAuth();
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const localeCode = isTr ? 'tr-TR' : 'en-US';
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);

  const [integrations, setIntegrations] = useState<PavoPayIntegrationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<PavoPayIntegrationItem | null>(null);
  const [formUsername, setFormUsername] = useState('');
  const [formPassword, setFormPassword] = useState('');
  const [formBranchId, setFormBranchId] = useState<string>('');
  const [formBranchName, setFormBranchName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState<number | null>(null);

  useEffect(() => {
    loadBranches();
  }, []);

  const loadIntegrations = useCallback(async () => {
    setLoading(true);
    try {
      const response = await accountingApi.get(ACCOUNTING_API.PAVOPAY_INTEGRATION_LIST);
      const result = response.data;
      const items = (result.data || []).map((item: any) => ({
        id: item.id ?? item.Id,
        branchId: item.branchId ?? item.BranchId ?? null,
        branchName: item.branchName ?? item.BranchName ?? null,
        username: item.username ?? item.Username ?? '',
        encryptedPassword: item.encryptedPassword ?? item.EncryptedPassword ?? '',
        isActive: item.isActive ?? item.IsActive ?? false,
        lastAuthDate: item.lastAuthDate ?? item.LastAuthDate ?? null,
        createdAt: item.createdAt ?? item.CreatedAt ?? '',
        updatedAt: item.updatedAt ?? item.UpdatedAt ?? null,
      }));
      setIntegrations(items);
      setError(null);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadIntegrations();
  }, [loadIntegrations]);

  const handleOpenAdd = () => {
    setEditingItem(null);
    setFormUsername('');
    setFormPassword('');
    setFormBranchId('');
    setFormBranchName('');
    setShowPassword(false);
    setModalOpen(true);
  };

  const handleOpenEdit = (item: PavoPayIntegrationItem) => {
    setEditingItem(item);
    setFormUsername(item.username);
    setFormPassword('');
    setFormBranchId(item.branchId?.toString() || '');
    setFormBranchName(item.branchName || '');
    setShowPassword(false);
    setModalOpen(true);
  };

  const handleBranchChange = (branchId: string) => {
    setFormBranchId(branchId);
    if (branchId && branchId !== 'null') {
      const found = branches.find(b => b.id === branchId);
      setFormBranchName(found?.name || '');
    } else {
      setFormBranchName('');
    }
  };

  const handleSave = async () => {
    if (!formUsername.trim()) {
      setSnackbar({ open: true, message: tt('Kullanıcı adı zorunludur.', 'Username is required.'), severity: 'error' });
      return;
    }
    if (!editingItem && !formPassword.trim()) {
      setSnackbar({ open: true, message: tt('Şifre zorunludur.', 'Password is required.'), severity: 'error' });
      return;
    }

    setSaving(true);
    try {
      const payload: any = {
        Username: formUsername.trim(),
        BranchId: formBranchId && formBranchId !== 'null' ? parseInt(formBranchId) : null,
        BranchName: formBranchName.trim() || null,
        UpdatedBy: userDetails?.email || 'Unknown'
      };
      if (editingItem) {
        payload.Id = editingItem.id;
      }
      if (formPassword.trim()) {
        payload.EncryptedPassword = formPassword.trim();
      }

      await accountingApi.post(ACCOUNTING_API.PAVOPAY_INTEGRATION_SAVE, payload);
      setSnackbar({ open: true, message: editingItem ? tt('Entegrasyon güncellendi.', 'Integration updated.') : tt('Entegrasyon oluşturuldu.', 'Integration created.'), severity: 'success' });
      setModalOpen(false);
      await loadIntegrations();
    } catch (err) {
      setSnackbar({ open: true, message: handleApiError(err), severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(tt('Bu entegrasyonu silmek istediğinizden emin misiniz?', 'Are you sure you want to delete this integration?'))) return;
    try {
      await accountingApi.post(ACCOUNTING_API.PAVOPAY_INTEGRATION_DELETE, { Id: id });
      setSnackbar({ open: true, message: tt('Entegrasyon silindi.', 'Integration deleted.'), severity: 'success' });
      await loadIntegrations();
    } catch (err) {
      setSnackbar({ open: true, message: handleApiError(err), severity: 'error' });
    }
  };

  const handleToggleActive = async (id: number) => {
    try {
      await accountingApi.post(ACCOUNTING_API.PAVOPAY_INTEGRATION_TOGGLE_ACTIVE, { Id: id });
      await loadIntegrations();
    } catch (err) {
      setSnackbar({ open: true, message: handleApiError(err), severity: 'error' });
    }
  };

  const handleAuthTest = async (id: number) => {
    setTesting(id);
    try {
      const response = await accountingApi.post(ACCOUNTING_API.PAVOPAY_INTEGRATION_AUTH_TEST, { Id: id });
      const result = response.data;
      if (result.success) {
        setSnackbar({ open: true, message: tt('Bağlantı testi başarılı!', 'Connection test successful!'), severity: 'success' });
        await loadIntegrations();
      } else {
        setSnackbar({ open: true, message: result.message || tt('Bağlantı testi başarısız.', 'Connection test failed.'), severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: handleApiError(err), severity: 'error' });
    } finally {
      setTesting(null);
    }
  };

  const columns: DataTableColumn<PavoPayIntegrationItem>[] = [
    {
      id: 'status',
      label: tt('Durum', 'Status'),
      width: '80px',
      align: 'center',
      render: (row) => (
        <Tooltip title={row.isActive ? tt('Aktif — tıklayarak pasif yapın', 'Active — click to set passive') : tt('Pasif — tıklayarak aktif yapın', 'Passive — click to set active')}>
          <IconButton size="small" onClick={() => handleToggleActive(row.id)}>
            {row.isActive
              ? <CheckCircleIcon color="success" />
              : <CancelIcon color="disabled" />}
          </IconButton>
        </Tooltip>
      ),
    },
    {
      id: 'username',
      label: tt('Kullanıcı Adı', 'Username'),
      field: 'username',
      sortable: true,
      minWidth: '180px',
    },
    {
      id: 'branchName',
      label: tt('Şube', 'Branch'),
      field: 'branchName',
      sortable: true,
      minWidth: '150px',
      render: (row) => row.branchName || <Typography variant="body2" color="text.secondary">—</Typography>,
    },
    {
      id: 'lastAuthDate',
      label: tt('Son Auth', 'Last Auth'),
      field: 'lastAuthDate',
      sortable: true,
      minWidth: '160px',
      render: (row) => row.lastAuthDate
        ? <>{new Date(row.lastAuthDate).toLocaleString(localeCode)}</>
        : <Typography variant="body2" color="text.secondary">{tt('Henüz yok', 'Not yet')}</Typography>,
    },
    {
      id: 'createdAt',
      label: tt('Oluşturulma', 'Created'),
      field: 'createdAt',
      sortable: true,
      minWidth: '140px',
      render: (row) => <>{row.createdAt ? new Date(row.createdAt).toLocaleDateString(localeCode) : '—'}</>,
    },
    {
      id: 'actions',
      label: tt('İşlemler', 'Actions'),
      width: '160px',
      align: 'center',
      render: (row) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
          <Tooltip title={tt('Bağlantı Test Et', 'Test Connection')}>
            <IconButton size="small" color="info" onClick={() => handleAuthTest(row.id)} disabled={testing === row.id}>
              {testing === row.id ? <CircularProgress size={18} /> : <VerifiedUserIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Tooltip title={tt('Düzenle', 'Edit')}>
            <IconButton size="small" color="primary" onClick={() => handleOpenEdit(row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={tt('Sil', 'Delete')}>
            <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  // "Şubeye Ait Değil" olan entry'yi filtrele
  const selectableBranches = branches.filter(b => b.id !== 'null');

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => navigate('/e-fatura')}>
            <ArrowBackIcon />
          </IconButton>
          <SecurityIcon color="info" />
          <Typography variant="h5" fontWeight="bold">
            {tt('PavoPay Entegrasyonları', 'PavoPay Integrations')}
          </Typography>
          <Chip label={`${integrations.length} ${tt('kayıt', 'records')}`} size="small" color="info" variant="outlined" />
        </Box>
        <Button
          variant="contained"
          color="info"
          startIcon={<AddIcon />}
          onClick={handleOpenAdd}
        >
          {tt('Yeni Entegrasyon', 'New Integration')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined">
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : integrations.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <SecurityIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography variant="h6" color="text.secondary">
              {tt('Henüz PavoPay entegrasyonu eklenmemiş', 'No PavoPay integration added yet')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {tt('Aktif Bank e-Dönüşüm sistemi ile entegre olmak için yeni bir entegrasyon ekleyin.', 'Add a new integration to connect with Aktif Bank e-Transformation system.')}
            </Typography>
            <Button variant="contained" color="info" startIcon={<AddIcon />} onClick={handleOpenAdd}>
              {tt('İlk Entegrasyonu Ekle', 'Add First Integration')}
            </Button>
          </Box>
        ) : (
          <AccountingDataTable<PavoPayIntegrationItem>
            columns={columns}
            rows={integrations}
            getRowId={(row) => String(row.id)}
          />
        )}
      </Paper>

      {/* Add/Edit Modal */}
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: 'info.main', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon />
            <Typography variant="h6" fontWeight="bold">
              {editingItem ? tt('PavoPay Entegrasyonu Düzenle', 'Edit PavoPay Integration') : tt('Yeni PavoPay Entegrasyonu', 'New PavoPay Integration')}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>{tt('Şube (Opsiyonel)', 'Branch (Optional)')}</InputLabel>
                <Select
                  value={formBranchId}
                  label={tt('Şube (Opsiyonel)', 'Branch (Optional)')}
                  onChange={(e) => handleBranchChange(e.target.value)}
                >
                  <MenuItem value="">
                    <em>{tt('Şube seçilmedi', 'No branch selected')}</em>
                  </MenuItem>
                  {selectableBranches.map((branch) => (
                    <MenuItem key={branch.id} value={branch.id}>
                      {branch.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label={tt('Kullanıcı Adı', 'Username')}
                value={formUsername}
                onChange={(e) => setFormUsername(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required={!editingItem}
                label={tt('Şifre', 'Password')}
                type={showPassword ? 'text' : 'password'}
                value={formPassword}
                onChange={(e) => setFormPassword(e.target.value)}
                helperText={editingItem ? tt('Boş bırakılırsa mevcut şifre korunur.', 'If left blank, current password is preserved.') : ''}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setModalOpen(false)} color="inherit" disabled={saving}>
            {tt('İptal', 'Cancel')}
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="info"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SecurityIcon />}
          >
            {saving ? tt('Kaydediliyor...', 'Saving...') : tt('Kaydet', 'Save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default PavoPayIntegrations;
